import { createContext, useCallback, useMemo } from 'react';
import { trackPromise } from 'react-promise-tracker';
import { IContextInitial } from '../../model/ContextModal';
import { ILoginState } from '../../model/LoginModel';
import { SessionStorage } from '../../services/storage';
import { useLoginStore } from '../../stores/LoginStore';
import { isNotEmptyObject } from '../../utils/app-common';
import { useNotificationStore } from '../../stores/notificationStore';

interface IContext {
    children: JSX.Element
}

const UserContext = createContext({} as IContextInitial);

const UserContextComponent = ({ children }: IContext) => {

    const GetEmployeeDetailAction = useLoginStore((state: ILoginState) => state?.GetEmployeeDetailAction);
    const GetMasterDetailAction = useLoginStore((state: ILoginState) => state?.GetMasterDetailAction);
    const GetCommodityAction = useLoginStore((state: ILoginState) => state?.GetCommodityAction);
    const ClearLoginStore = useLoginStore((state: ILoginState) => state?.ClearLoginStore);
    const notificationToken = useNotificationStore((state) => state?.notificationToken);
    const setNotificationToken = useNotificationStore((state) => state?.setNotificationToken);

    const employeeDetailState = useLoginStore((state: ILoginState) => state?.employeeDetail) || {};
    const masterDetailState = useLoginStore((state: ILoginState) => state?.masterDetail) || {};
    const session = SessionStorage.get('employee') || '{}';
    const appStorage = localStorage.getItem('app-storage') || '{}';
    const employeeDetail = isNotEmptyObject(employeeDetailState) ? employeeDetailState : JSON.parse(session);
    const masterDetail = isNotEmptyObject(masterDetailState) ? masterDetailState : JSON.parse(appStorage);

    const promiseAll = useCallback(async (username: string) => {
        // Only load employee details during login - master details loaded separately after login completion
        await trackPromise(GetEmployeeDetailAction(username), 'initial-area');
    }, [GetEmployeeDetailAction])

    // Separate function to load master details after login completion
    const loadMasterDetails = useCallback(async () => {
        await trackPromise(Promise.all([GetMasterDetailAction(), GetCommodityAction()]), 'get-master-area');
    }, [GetMasterDetailAction, GetCommodityAction]);

    const setEmployeeDetailHandle = useCallback((username: string) => {
        if (username) {
            promiseAll(username)
        }
    }, [promiseAll])

    const clearDetailHandle = useCallback(() => {
        SessionStorage.clear();
        ClearLoginStore();
    }, [ClearLoginStore])

    const values: any = useMemo(() => ({
        setEmployeeDetailHandle,
        clearDetailHandle,
        loadMasterDetails, // Add master details loading function
        employeeDetail,
        masterDetail,
        notificationToken,
        setNotificationToken
    }), [clearDetailHandle,
        employeeDetail,
        masterDetail,
        loadMasterDetails,
        notificationToken,
        setEmployeeDetailHandle,
        setNotificationToken])


    return (
        <UserContext.Provider value={values}>
            {children}
        </UserContext.Provider>

    )
}
export { UserContext };
export default UserContextComponent